// Entrepreneurship & Civic Engagement scoring functions

import type {
    EntrepreneurshipQuestionType,
    EntrepreneurshipScores,
    EntrepreneurshipCategory,
    EntrepreneurshipScenario
} from '../types/surveys/EntrepreneurshipSurvey.ts'
import { EntrepreneurshipCategory as Category } from '../types/surveys/EntrepreneurshipSurvey.ts'

// Weight for scenarios in scoring (scenarios reveal actual behavior, so weighted more heavily)
const SCENARIO_WEIGHT = 1.5

/**
 * Convert scenario level response to numeric score (1-5 scale)
 * low = 1, medium = 3, high = 5
 */
function levelToScore(level: 'low' | 'medium' | 'high'): number {
    switch (level) {
        case 'low':
            return 1
        case 'medium':
            return 3
        case 'high':
            return 5
    }
}

/**
 * Calculate Entrepreneurship scores from answered questions
 * Returns scores normalized to 0-100 scale
 */
export function calculateEntrepreneurshipScores(questions: EntrepreneurshipQuestionType[]): EntrepreneurshipScores {
    const scores: EntrepreneurshipScores = {
        [Category.INITIATIVE]: 0,
        [Category.RISK_TAKING]: 0,
        [Category.INNOVATION]: 0,
        [Category.PROBLEM_SOLVING]: 0,
        [Category.COMMUNITY_INVOLVEMENT]: 0,
        [Category.SOCIAL_RESPONSIBILITY]: 0,
        [Category.DEMOCRATIC_PARTICIPATION]: 0
    }

    // Group questions by category
    const categoryQuestions: Record<EntrepreneurshipCategory, EntrepreneurshipQuestionType[]> = {
        [Category.INITIATIVE]: [],
        [Category.RISK_TAKING]: [],
        [Category.INNOVATION]: [],
        [Category.PROBLEM_SOLVING]: [],
        [Category.COMMUNITY_INVOLVEMENT]: [],
        [Category.SOCIAL_RESPONSIBILITY]: [],
        [Category.DEMOCRATIC_PARTICIPATION]: []
    }

    questions.forEach((q) => {
        if (q.value !== undefined) {
            categoryQuestions[q.category].push(q)
        }
    })

    // Calculate score for each category
    Object.keys(categoryQuestions).forEach((category) => {
        const catKey = category as EntrepreneurshipCategory
        const catQs = categoryQuestions[catKey]

        if (catQs.length === 0) {
            scores[catKey] = 0
            return
        }

        let sum = 0
        catQs.forEach((q) => {
            if (q.value !== undefined) {
                // Reverse scoring for reversed questions
                const score = q.reversed ? 6 - q.value : q.value
                sum += score
            }
        })

        // Normalize to 0-100 scale
        const rawScore = sum
        const minPossible = catQs.length * 1
        const maxPossible = catQs.length * 5

        scores[catKey] = Math.round(((rawScore - minPossible) / (maxPossible - minPossible)) * 100)
    })

    return scores
}

/**
 * Calculate Entrepreneurship scores including both Likert questions AND behavioral scenarios
 * Scenarios are weighted more heavily (1.5x) as they reveal actual behavioral responses
 * Returns scores normalized to 0-100 scale
 */
export function calculateCombinedEntrepreneurshipScores(
    questions: EntrepreneurshipQuestionType[],
    scenarios?: EntrepreneurshipScenario[]
): EntrepreneurshipScores {
    const scores: EntrepreneurshipScores = {
        [Category.INITIATIVE]: 0,
        [Category.RISK_TAKING]: 0,
        [Category.INNOVATION]: 0,
        [Category.PROBLEM_SOLVING]: 0,
        [Category.COMMUNITY_INVOLVEMENT]: 0,
        [Category.SOCIAL_RESPONSIBILITY]: 0,
        [Category.DEMOCRATIC_PARTICIPATION]: 0
    }

    const weights: Record<EntrepreneurshipCategory, number> = {
        [Category.INITIATIVE]: 0,
        [Category.RISK_TAKING]: 0,
        [Category.INNOVATION]: 0,
        [Category.PROBLEM_SOLVING]: 0,
        [Category.COMMUNITY_INVOLVEMENT]: 0,
        [Category.SOCIAL_RESPONSIBILITY]: 0,
        [Category.DEMOCRATIC_PARTICIPATION]: 0
    }

    // Score Likert questions (weight = 1)
    questions.forEach((q) => {
        if (q.value !== undefined) {
            // Reverse scoring for reversed questions
            const score = q.reversed ? 6 - q.value : q.value
            scores[q.category] += score
            weights[q.category] += 1
        }
    })

    // Score scenarios (weight = SCENARIO_WEIGHT, typically 1.5)
    if (scenarios && scenarios.length > 0) {
        scenarios.forEach((s) => {
            if (s.selectedOptionId) {
                const selectedOption = s.options.find((opt) => opt.id === s.selectedOptionId)
                if (selectedOption) {
                    const score = levelToScore(selectedOption.level)
                    scores[s.category] += score * SCENARIO_WEIGHT
                    weights[s.category] += SCENARIO_WEIGHT
                }
            }
        })
    }

    // Calculate weighted averages and normalize to 0-100 scale
    const normalizedScores: EntrepreneurshipScores = {} as EntrepreneurshipScores
    Object.entries(scores).forEach(([category, weightedSum]) => {
        const catKey = category as EntrepreneurshipCategory
        const weight = weights[catKey]

        if (weight > 0) {
            // Average score is weightedSum / weight (on 1-5 scale)
            const averageScore = weightedSum / weight
            // Normalize to 0-100: (score - 1) / 4 * 100
            normalizedScores[catKey] = Math.round(((averageScore - 1) / 4) * 100)
        } else {
            normalizedScores[catKey] = 0
        }
    })

    return normalizedScores
}

/**
 * Calculate entrepreneurship dimension score (4 categories)
 */
export function calculateEntrepreneurshipDimensionScore(scores: EntrepreneurshipScores): number {
    const entrepreneurshipCategories = [
        scores[Category.INITIATIVE],
        scores[Category.RISK_TAKING],
        scores[Category.INNOVATION],
        scores[Category.PROBLEM_SOLVING]
    ]

    const sum = entrepreneurshipCategories.reduce((acc, score) => acc + score, 0)
    return Math.round(sum / entrepreneurshipCategories.length)
}

/**
 * Calculate civic engagement dimension score (3 categories)
 */
export function calculateCivicEngagementDimensionScore(scores: EntrepreneurshipScores): number {
    const civicCategories = [
        scores[Category.COMMUNITY_INVOLVEMENT],
        scores[Category.SOCIAL_RESPONSIBILITY],
        scores[Category.DEMOCRATIC_PARTICIPATION]
    ]

    const sum = civicCategories.reduce((acc, score) => acc + score, 0)
    return Math.round(sum / civicCategories.length)
}

/**
 * Get interpretation for an entrepreneurship category score
 */
export function interpretEntrepreneurshipScore(category: EntrepreneurshipCategory, score: number): string {
    const level = score >= 70 ? 'high' : score >= 30 ? 'medium' : 'low'

    const interpretations: Record<EntrepreneurshipCategory, Record<string, string>> = {
        [Category.INITIATIVE]: {
            high: 'Máš vysokú mieru iniciatívy. Proaktívne vyhľadávaš príležitosti a začínaš nové projekty bez toho, aby ťa niekto musel poprosiť.',
            medium: 'Máš priemernú iniciatívu. Niekedy konáš proaktívne, inokedy čakáš na pokyny alebo povzbudenie.',
            low: 'Máš nižšiu mieru iniciatívy. Radšej čakáš na inštruie než aby si konal/konala samostatne. Skús identifikovať malé príležitosti, kde by si mohol/mohla začať.'
        },
        [Category.RISK_TAKING]: {
            high: 'Máš vysokú ochotu riskovať. Nebojíš sa experimentovať a skúšať nové veci, aj keď výsledok nie je istý.',
            medium: 'Máš priemernú ochotu riskovať. Dokážeš riskovať v rámci bezpečných hraníc.',
            low: 'Preferuješ istotu pred rizikom. To môže byť výhoda v stabilných situáciách, ale môže ťa obmedzovať v inovácii. Skús začať s malými, kontrolovanými rizikami.'
        },
        [Category.INNOVATION]: {
            high: 'Máš vysokú mieru inovačného myslenia. Prirodzene vymýšľaš nové riešenia a kreatívne prístupy k problémom.',
            medium: 'Máš priemernú schopnosť inovácie. Dokážeš byť kreatívny/kreatívna, keď je to potrebné.',
            low: 'Preferuješ osvedčené postupy pred inovatívnymi prístupmi. Skús experimentovať s novými metódami v bezpečnom prostredí.'
        },
        [Category.PROBLEM_SOLVING]: {
            high: 'Máš výborné schopnosti riešenia problémov. Výzvy ťa motivujú a dokážeš nájsť riešenia aj pre komplexné situácie.',
            medium: 'Máš dobré schopnosti riešenia problémov. Dokážeš riešiť bežné problémy, ale komplexné situácie ťa môžu frustrovat.',
            low: 'Riešenie problémov nie je tvoja silná stránka. Problémy ťa môžu odrádzať. Skús si rozdeliť veľké problémy na menšie časti.'
        },
        [Category.COMMUNITY_INVOLVEMENT]: {
            high: 'Si vysoko angažovaný/angažovaná v komunite. Pravidelne sa zapájaš do komunitných aktivít a pomáhaš svojmu okoliu.',
            medium: 'Si občas zapojený/zapojená v komunite. Pomáhaš, keď máš čas alebo keď ťa niekto požiada.',
            low: 'Máš nízku mieru komunitného zapojenia. Skús nájsť jednu aktivitu, ktorá ťa zaujíma, a začni pomaly.'
        },
        [Category.SOCIAL_RESPONSIBILITY]: {
            high: 'Máš vysoké povedomie o sociálnej zodpovednosti. Zvažuješ dopad svojich rozhodnutí na spoločnosť a aktívne sa snažíš prispievať k lepšiemu svetu.',
            medium: 'Máš priemernú sociálnu zodpovednosť. Záleží ti na spoločenských témach, ale nie vždy konáš podľa toho.',
            low: 'Sociálne problémy nie sú tvou prioritou. Skús sa zamyslieť, ako tvoje každodenné rozhodnutia ovplyvňujú ostatných.'
        },
        [Category.DEMOCRATIC_PARTICIPATION]: {
            high: 'Si vysoko angažovaný/angažovaná v demokratických procesoch. Pravidelne voliš, sleduješ verejné záležitosti a zapájaš sa do občianskych iniciatív.',
            medium: 'Občas sa zapájaš do demokratických procesov. Voliš, ale inak si menej aktívny/aktívna.',
            low: 'Máš nízku mieru demokratického zapojenia. Politika a verejné záležitosti ťa nezaujímajú. Pamätaj, že tvoj hlas má význam.'
        }
    }

    return interpretations[category][level] || ''
}

/**
 * Get overall interpretation for both dimensions
 */
export function interpretOverallEntrepreneurship(
    entrepreneurshipScore: number,
    civicEngagementScore: number
): {
    entrepreneurshipLevel: string
    civicLevel: string
    description: string
    strengths: string[]
    developmentAreas: string[]
} {
    const eLevel = entrepreneurshipScore >= 70 ? 'high' : entrepreneurshipScore >= 30 ? 'medium' : 'low'
    const cLevel = civicEngagementScore >= 70 ? 'high' : civicEngagementScore >= 30 ? 'medium' : 'low'

    const levelLabels = {
        high: 'Vysoká',
        medium: 'Priemerná',
        low: 'Nízka'
    }

    let description = ''
    const strengths: string[] = []
    const developmentAreas: string[] = []

    // Entrepreneurship level
    if (eLevel === 'high') {
        description +=
            'Máš silné podnikateľské myslenie. Si proaktívny/proaktívna, inovatívny/inovatívna a dokážeš riešiť problémy. '
        strengths.push('Podnikateľské myslenie', 'Iniciatíva', 'Inovácia')
    } else if (eLevel === 'medium') {
        description += 'Máš rozvinuté podnikateľské myslenie v niektorých oblastiach. '
        developmentAreas.push('Rozvíjaj iniciatívu a odvahu riskovať')
    } else {
        description += 'Podnikateľské myslenie nie je tvoja silná stránka. '
        developmentAreas.push('Pracuj na proaktivite', 'Skúšaj nové prístupy', 'Rozvíjaj schopnosť riešenia problémov')
    }

    // Civic engagement level
    if (cLevel === 'high') {
        description +=
            'Si vysoko angažovaný/angažovaná občan/občianka. Aktívne sa zapájaš do komunity a demokratických procesov.'
        strengths.push('Občianska angažovanosť', 'Sociálna zodpovednosť', 'Komunitné zapojenie')
    } else if (cLevel === 'medium') {
        description += 'Občas sa zapájaš do občianskych aktivít.'
        developmentAreas.push('Zvýš svoju občiansku angažovanosť')
    } else {
        description += 'Máš nízku mieru občianskej angažovanosti.'
        developmentAreas.push(
            'Nájdi komunitnú aktivitu, ktorá ťa zaujíma',
            'Začni sledovať verejné záležitosti',
            'Zúčastňuj sa volieb'
        )
    }

    return {
        entrepreneurshipLevel: levelLabels[eLevel],
        civicLevel: levelLabels[cLevel],
        description,
        strengths,
        developmentAreas
    }
}
